import { useMemo, useState } from 'react';
import { AppBar, Avatar, Box, Button, Divider, ListItemIcon, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, Logout as LogoutIcon } from '@mui/icons-material';

import { User } from '../../services/types';
import { useUser } from '../../contexts';
import { usePageSessionType } from '../../hooks';
import { showAlert, logout, getCachedUserLocations, isClinicalSession } from '../../utilities';

import LocationAutocomplete from './LocationAutocomplete';

export const handleLogout = () => {
  showAlert({
    title: 'Are you sure?',
    text: 'Do you want to log out!',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes',
  }).then((result) => {
    if (result.isConfirmed) {
      showAlert({
        title: 'LOG OUT!',
        text: 'You have been logged out.',
        icon: 'success',
        showCancelButton: false,
        timer: 2000,
      });
      logout();
    }
  });
};

const Appbar = () => {
  const {
    state: { user },
  } = useUser();
  const sessionType = usePageSessionType();
  const showClinicalLocation = isClinicalSession(sessionType);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const displayUser: User = user ?? ({} as User);
  const menuOpen = Boolean(anchorEl);

  const currentLocationLabel = useMemo(() => {
    if (!showClinicalLocation || !user) return null;
    const code = user.locationCode?.trim();
    if (!code) return null;

    const fromUser = user.locationDescription?.trim();
    if (fromUser) return fromUser;
    
    const match = getCachedUserLocations()?.find(
      (o) => o.code.toLowerCase() === code.toLowerCase()
    );
    return match?.description || code;
  }, [user, showClinicalLocation]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar position="static" sx={{ flexGrow: 1 }}>
      <Toolbar sx={{ justifyContent: 'flex-end' }}>
        <Button
          onClick={handleOpenMenu}
          color="inherit"
          startIcon={<Avatar src={displayUser.avatar} sx={{ width: 40, height: 40 }} />}
          endIcon={<KeyboardArrowDownIcon />}
          size="large"
          aria-label="account menu"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            <Typography variant="h6" component="div" sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}>
              {displayUser.name}
            </Typography>
            {currentLocationLabel ? (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  opacity: 0.9,
                  textTransform: 'none',
                  fontWeight: 400,
                  lineHeight: 1.2,
                  mt: 0.25,
                }}
              >
                {currentLocationLabel}
              </Typography>
            ) : null}
          </Box>
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          MenuListProps={{ sx: { minWidth: 320 } }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          {showClinicalLocation ? (
            <>
              <LocationAutocomplete onCloseMenu={handleCloseMenu} />
              <Divider sx={{ mb: 1 }} />
            </>
          ) : null}
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Appbar;
